// Home page — Justin's "what's happening in my business today" dashboard.
//
// All M0/M1 dev surfaces (Worker health, RfqExtractTest, OutlookDraftTest,
// RemindersPanel, WatcherAdmin, DevToolsCard, Setup checklist) moved to a
// separate /dev page owned by another agent. This file is now purely
// business-facing.
//
// Single-column, scannable on a laptop. Sections in order:
//   1. Greeting strip   — "Good morning, Justin" + today + "Process RFQ inbox" CTA
//   2. KPI cards row    — Open quotes / Active orders / Customers (3-up)
//   3. Recent activity  — merged quote + order _audit feed, last 14d, top 10
//   4. Pending follow-ups — quotes with followUp.nextDueDate due/overdue
//   5. Quick actions    — "+ New customer / quote / part" + "Customers list"
//
// Data strategy: fire listQuotes / listOrders / listCustomers once on mount in
// parallel. Each section has its own loading skeleton + error/retry — a single
// failing fetch doesn't blank the whole page (Justin will land here when
// Firebase is flaky and we want him to still see what loaded).

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listCustomers, listOrders, listQuotes } from '@/shared/crm';
import { currentFirebaseUser } from '@/shared/firebase';
import type {
  AuditEntry,
  Customer,
  Order,
  OrderStatus,
  Quote,
  QuoteStatus,
} from '@/shared/crmTypes';
import { NewCustomerModal } from '@/components/NewCustomerModal';
import { NewQuoteModal } from '@/components/NewQuoteModal';
import { NewPartModal } from '@/components/NewPartModal';

// =====================================================
// Constants
// =====================================================

const OPEN_QUOTE_STATUSES: QuoteStatus[] = ['draft', 'sent', 'follow_up', 'negotiating'];
const ACTIVE_ORDER_STATUSES: OrderStatus[] = ['draft', 'in_production', 'shipped'];

const DAY_MS = 24 * 60 * 60 * 1000;
const ACTIVITY_WINDOW_DAYS = 14;
const ACTIVITY_LIMIT = 10;
const FOLLOWUP_LIMIT = 5;
const NEW_CUSTOMER_WINDOW_DAYS = 30;
const SHIPPING_THIS_WEEK_DAYS = 7;

// =====================================================
// Helpers
// =====================================================

function greetingForHour(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/** "justin@keymf.com" -> "Justin". Capitalizes the local-part of the email. */
function firstNameFromEmail(email: string | null | undefined): string {
  if (!email) return 'there';
  const local = email.split('@')[0] ?? '';
  if (!local) return 'there';
  // Split on common separators in case it's e.g. "justin.jenkins"
  const stem = local.split(/[.\-_]/)[0] ?? local;
  if (!stem) return 'there';
  return stem.charAt(0).toUpperCase() + stem.slice(1).toLowerCase();
}

function fmtCurrencyCompact(n: number): string {
  // Below $10k stays as plain dollars so small shops don't see "$2.3k" for
  // a single quote — feels condescending. Above that, compact for density.
  if (n < 10_000) {
    return n.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });
  }
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1,
  });
}

function fmtTodayDate(): string {
  return new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/** "5m ago", "3h ago", "2d ago" — keeps the activity feed scannable. */
function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '—';
  if (ms < 60_000) return 'just now';
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days}d ago`;
  // Past 30 days = a month-ish; we still want it readable even though it'll
  // be filtered out of the activity feed (kept for follow-up overdue cases).
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

/** Days overdue (positive = past, 0 = today). nextDueDate is an ISO date. */
function daysSinceIsoDate(iso: string): number {
  const due = new Date(iso).getTime();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.floor((today.getTime() - due) / DAY_MS);
}

function ymdAddDays(base: Date, days: number): number {
  return base.getTime() + days * DAY_MS;
}

// =====================================================
// Activity feed types
// =====================================================

/**
 * One row in the merged feed. We capture enough context to render + link
 * without re-querying anything — the entity type drives the icon + the
 * route, and the description is built once at merge time.
 */
interface ActivityRow {
  ts: string; // ISO 8601
  entityType: 'quote' | 'order';
  entityId: string;
  entityNumber: string; // 'Q-1042' / 'O-2245'
  customerName: string;
  action: AuditEntry['action'];
  user: string;
  description: string;
}

/** Build a human-readable description for a single audit entry. */
function describeAuditEntry(entry: AuditEntry, entityNumber: string): string {
  // status_change carries `fields.status.{from,to}` — surface the destination.
  if (entry.action === 'status_change') {
    const f = entry.fields?.status;
    if (f && typeof f === 'object' && 'to' in f) {
      return `${entityNumber} → ${String(f.to)}`;
    }
    return `${entityNumber} status changed`;
  }
  if (entry.action === 'create') return `${entityNumber} created`;
  if (entry.action === 'update') return `${entityNumber} updated`;
  if (entry.action === 'approve') return `${entityNumber} approved`;
  if (entry.action === 'reject') return `${entityNumber} rejected`;
  return `${entityNumber} ${entry.action}`;
}

function mergeActivity(quotes: Quote[], orders: Order[]): ActivityRow[] {
  const cutoff = Date.now() - ACTIVITY_WINDOW_DAYS * DAY_MS;
  const rows: ActivityRow[] = [];

  const pushFrom = (
    audit: AuditEntry[] | undefined,
    entityType: ActivityRow['entityType'],
    entityId: string,
    entityNumber: string,
    customerName: string,
  ) => {
    if (!audit) return;
    for (const entry of audit) {
      const ts = new Date(entry.ts).getTime();
      if (Number.isNaN(ts) || ts < cutoff) continue;
      rows.push({
        ts: entry.ts,
        entityType,
        entityId,
        entityNumber,
        customerName,
        action: entry.action,
        user: entry.user,
        description: describeAuditEntry(entry, entityNumber),
      });
    }
  };

  for (const q of quotes) {
    pushFrom(q._audit, 'quote', q.id, q.number, q.customerNameSnapshot);
  }
  for (const o of orders) {
    pushFrom(o._audit, 'order', o.id, o.number, o.customerNameSnapshot);
  }

  rows.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
  return rows.slice(0, ACTIVITY_LIMIT);
}

// =====================================================
// Page
// =====================================================

interface LoadState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

function initialLoad<T>(): LoadState<T> {
  return { data: null, loading: true, error: null };
}

export function HomePage() {
  const navigate = useNavigate();

  const [quotesState, setQuotesState] = useState<LoadState<Quote[]>>(initialLoad);
  const [ordersState, setOrdersState] = useState<LoadState<Order[]>>(initialLoad);
  const [customersState, setCustomersState] = useState<LoadState<Customer[]>>(initialLoad);

  // Modal open state for the quick-actions row. Each modal owns its own
  // navigation-on-create via the onCreated callbacks below.
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [partModalOpen, setPartModalOpen] = useState(false);

  async function loadQuotes() {
    setQuotesState({ data: null, loading: true, error: null });
    try {
      const data = await listQuotes({});
      setQuotesState({ data, loading: false, error: null });
    } catch (e) {
      setQuotesState({ data: null, loading: false, error: (e as Error).message });
    }
  }
  async function loadOrders() {
    setOrdersState({ data: null, loading: true, error: null });
    try {
      const data = await listOrders({});
      setOrdersState({ data, loading: false, error: null });
    } catch (e) {
      setOrdersState({ data: null, loading: false, error: (e as Error).message });
    }
  }
  async function loadCustomers() {
    setCustomersState({ data: null, loading: true, error: null });
    try {
      const data = await listCustomers();
      setCustomersState({ data, loading: false, error: null });
    } catch (e) {
      setCustomersState({ data: null, loading: false, error: (e as Error).message });
    }
  }

  useEffect(() => {
    // Fire all three in parallel — the dashboard wants everything at once;
    // sequencing them would feel slow on first load.
    void loadQuotes();
    void loadOrders();
    void loadCustomers();
  }, []);

  // ---------- Greeting ----------
  const user = currentFirebaseUser();
  const greeting = useMemo(() => greetingForHour(new Date().getHours()), []);
  const firstName = useMemo(() => firstNameFromEmail(user?.email), [user?.email]);
  const todayDate = useMemo(() => fmtTodayDate(), []);

  // ---------- KPIs ----------
  const openQuotes = useMemo(() => {
    const qs = quotesState.data ?? [];
    return qs.filter((q) => OPEN_QUOTE_STATUSES.includes(q.status));
  }, [quotesState.data]);

  // Top-tier total only — matches QuotesPage's "totalsByTier[0]" treatment.
  // Imported quotes carry importedTotal instead of totalsByTier, so honor that.
  const openQuoteValue = useMemo(() => {
    let sum = 0;
    for (const q of openQuotes) {
      if (q.imported && typeof q.importedTotal === 'number') {
        sum += q.importedTotal;
      } else if (typeof q.totalsByTier?.[0] === 'number') {
        sum += q.totalsByTier[0];
      }
    }
    return sum;
  }, [openQuotes]);

  const activeOrders = useMemo(() => {
    const os = ordersState.data ?? [];
    return os.filter((o) => ACTIVE_ORDER_STATUSES.includes(o.status));
  }, [ordersState.data]);

  const shippingThisWeek = useMemo(() => {
    const now = Date.now();
    const horizon = ymdAddDays(new Date(), SHIPPING_THIS_WEEK_DAYS);
    return activeOrders.filter((o) => {
      if (!o.promisedShipDate) return false;
      const t = new Date(o.promisedShipDate).getTime();
      return !Number.isNaN(t) && t >= now && t <= horizon;
    }).length;
  }, [activeOrders]);

  const customerCount = customersState.data?.length ?? 0;
  const newCustomersThisMonth = useMemo(() => {
    const cutoff = Date.now() - NEW_CUSTOMER_WINDOW_DAYS * DAY_MS;
    return (customersState.data ?? []).filter((c) => {
      const t = new Date(c.createdAt).getTime();
      return !Number.isNaN(t) && t >= cutoff;
    }).length;
  }, [customersState.data]);

  // ---------- Activity ----------
  const activity = useMemo(
    () => mergeActivity(quotesState.data ?? [], ordersState.data ?? []),
    [quotesState.data, ordersState.data],
  );

  // ---------- Pending follow-ups ----------
  const pendingFollowUps = useMemo(() => {
    const qs = quotesState.data ?? [];
    const today = new Date();
    today.setHours(23, 59, 59, 999); // include anything due today
    const todayMs = today.getTime();
    const due = qs
      .filter((q) => {
        const f = q.followUp;
        if (!f || f.paused) return false;
        if (!f.nextDueDate) return false;
        const t = new Date(f.nextDueDate).getTime();
        return !Number.isNaN(t) && t <= todayMs;
      })
      .map((q) => ({
        quote: q,
        // f.nextDueDate is asserted by the filter above.
        daysOverdue: daysSinceIsoDate(q.followUp!.nextDueDate!),
      }));
    // Most overdue first
    due.sort((a, b) => b.daysOverdue - a.daysOverdue);
    return due.slice(0, FOLLOWUP_LIMIT);
  }, [quotesState.data]);

  // ---------- Loading / error helpers ----------
  // We render a per-section skeleton only on initial load (data === null). Once
  // we have data, a re-fetch should leave the previous view in place to avoid
  // flicker — feels much steadier when Justin clicks Retry on a flaky link.

  function handleCustomerCreated(id: string) {
    setCustomerModalOpen(false);
    navigate(`/customers/${id}`);
  }
  function handleQuoteCreated(id: string) {
    setQuoteModalOpen(false);
    navigate(`/quotes/${id}`);
  }
  function handlePartCreated(id: string) {
    setPartModalOpen(false);
    navigate(`/parts/${id}`);
  }

  function activityClick(row: ActivityRow) {
    const path = row.entityType === 'quote' ? `/quotes/${row.entityId}` : `/orders`;
    // Orders detail page doesn't exist yet — fall back to the orders list so
    // the click still goes somewhere useful instead of dead-ending in 404.
    navigate(path);
  }

  return (
    <div className="max-w-5xl mx-auto space-y-5">
      {/* 1. Greeting strip */}
      <section className="bg-paper border border-line rounded p-4 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-navy">
            {greeting}, {firstName}
          </h1>
          <p className="text-sm text-text-mute mt-0.5">{todayDate}</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/rfq')}
          className="px-4 py-2 bg-orange text-white text-sm rounded font-semibold hover:opacity-90 transition-opacity whitespace-nowrap"
        >
          Process RFQ inbox →
        </button>
      </section>

      {/* 2. KPI cards row */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KpiCard
          label="Open quotes"
          loading={quotesState.loading && quotesState.data === null}
          error={quotesState.error}
          onRetry={loadQuotes}
          value={openQuotes.length}
          subtitle={
            openQuotes.length === 0
              ? 'No open quotes right now'
              : `${fmtCurrencyCompact(openQuoteValue)} across all open quotes`
          }
          onClick={() => navigate('/quotes')}
          accent="orange"
        />
        <KpiCard
          label="Active orders"
          loading={ordersState.loading && ordersState.data === null}
          error={ordersState.error}
          onRetry={loadOrders}
          value={activeOrders.length}
          subtitle={
            activeOrders.length === 0
              ? 'Nothing in production'
              : `${shippingThisWeek} shipping this week`
          }
          onClick={() => navigate('/orders')}
          accent="blue"
        />
        <KpiCard
          label="Customers"
          loading={customersState.loading && customersState.data === null}
          error={customersState.error}
          onRetry={loadCustomers}
          value={customerCount}
          subtitle={
            customerCount === 0
              ? 'Add your first customer'
              : `${newCustomersThisMonth} new this month`
          }
          onClick={() => navigate('/customers')}
          accent="green"
        />
      </section>

      {/* 3. Recent activity feed */}
      <section className="bg-paper border border-line rounded">
        <header className="px-4 py-3 border-b border-line flex items-center justify-between">
          <h2 className="font-semibold text-navy">Recent activity</h2>
          <span className="text-xs text-text-mute">Last {ACTIVITY_WINDOW_DAYS} days</span>
        </header>
        <div className="p-4">
          {(quotesState.loading || ordersState.loading) &&
          quotesState.data === null &&
          ordersState.data === null ? (
            <SkeletonRows count={5} />
          ) : quotesState.error || ordersState.error ? (
            <SectionError
              message={quotesState.error ?? ordersState.error ?? 'Could not load activity'}
              onRetry={() => {
                if (quotesState.error) void loadQuotes();
                if (ordersState.error) void loadOrders();
              }}
            />
          ) : activity.length === 0 ? (
            <p className="text-sm text-text-mute italic">
              No activity in the last {ACTIVITY_WINDOW_DAYS} days.
            </p>
          ) : (
            <ul className="divide-y divide-line">
              {activity.map((row, idx) => (
                <li
                  key={`${row.entityId}-${row.ts}-${idx}`}
                  className="py-2.5 flex items-center gap-3 cursor-pointer hover:bg-orange/5 -mx-2 px-2 rounded transition-colors"
                  onClick={() => activityClick(row)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      activityClick(row);
                    }
                  }}
                >
                  <span
                    aria-hidden
                    className={`text-lg shrink-0 w-7 text-center ${
                      row.entityType === 'quote' ? 'text-orange' : 'text-blue'
                    }`}
                  >
                    {row.entityType === 'quote' ? '📋' : '📦'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-text truncate">
                      <span className="font-mono font-semibold">{row.description}</span>
                      {row.customerName && (
                        <span className="text-text-mute"> · {row.customerName}</span>
                      )}
                    </div>
                    <div className="text-[11px] text-text-mute">
                      {relativeTime(row.ts)} · {row.user}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* 4. Pending follow-ups */}
      <section className="bg-paper border border-line rounded">
        <header className="px-4 py-3 border-b border-line flex items-center justify-between">
          <h2 className="font-semibold text-navy">Pending follow-ups</h2>
          {pendingFollowUps.length > 0 && (
            <span className="text-xs text-orange font-semibold">
              {pendingFollowUps.length} due
            </span>
          )}
        </header>
        <div className="p-4">
          {quotesState.loading && quotesState.data === null ? (
            <SkeletonRows count={3} />
          ) : quotesState.error ? (
            <SectionError message={quotesState.error} onRetry={loadQuotes} />
          ) : pendingFollowUps.length === 0 ? (
            <p className="text-sm text-text-mute italic">Nothing chasing you today.</p>
          ) : (
            <ul className="divide-y divide-line">
              {pendingFollowUps.map(({ quote, daysOverdue }) => (
                <li
                  key={quote.id}
                  className="py-2.5 flex items-center gap-3 flex-wrap"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-text">
                      <span className="font-mono font-semibold text-navy">{quote.number}</span>
                      <span className="text-text-mute"> · {quote.customerNameSnapshot}</span>
                    </div>
                    <div className="text-[11px] text-text-mute">
                      {daysOverdue === 0
                        ? 'Due today'
                        : `${daysOverdue} day${daysOverdue === 1 ? '' : 's'} overdue`}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate(`/quotes/${quote.id}`)}
                    className="text-xs px-2.5 py-1 rounded border border-line-dark text-navy hover:border-orange hover:text-orange transition-colors whitespace-nowrap"
                  >
                    Open quote
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* 5. Quick actions */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <QuickAction label="+ New customer" onClick={() => setCustomerModalOpen(true)} />
        <QuickAction label="+ New quote" onClick={() => setQuoteModalOpen(true)} />
        <QuickAction label="+ New part" onClick={() => setPartModalOpen(true)} />
        <QuickAction label="Customers list" onClick={() => navigate('/customers')} />
      </section>

      {/* Modal mounts — closed by default; only render when needed but keep
          them mounted so internal form state isn't lost on accidental close. */}
      <NewCustomerModal
        open={customerModalOpen}
        onClose={() => setCustomerModalOpen(false)}
        onCreated={handleCustomerCreated}
      />
      <NewQuoteModal
        open={quoteModalOpen}
        onClose={() => setQuoteModalOpen(false)}
        onCreated={handleQuoteCreated}
      />
      <NewPartModal
        open={partModalOpen}
        onClose={() => setPartModalOpen(false)}
        onCreated={handlePartCreated}
      />
    </div>
  );
}

// =====================================================
// Sub-components — kept local; not exported. If any of these grow useful
// elsewhere we'll lift them to /components.
// =====================================================

interface KpiCardProps {
  label: string;
  value: number;
  subtitle: string;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onClick: () => void;
  accent: 'orange' | 'blue' | 'green';
}

// Tailwind needs literal class strings — map accent token to a left-border class.
const KPI_ACCENT_BORDER: Record<KpiCardProps['accent'], string> = {
  orange: 'border-l-orange',
  blue: 'border-l-blue',
  green: 'border-l-green',
};

function KpiCard({
  label,
  value,
  subtitle,
  loading,
  error,
  onRetry,
  onClick,
  accent,
}: KpiCardProps) {
  if (loading) {
    return (
      <div className="bg-paper border border-line rounded p-4 animate-pulse">
        <div className="h-3 w-20 bg-line rounded mb-3" />
        <div className="h-8 w-16 bg-line rounded mb-2" />
        <div className="h-3 w-32 bg-line rounded" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-paper border border-line border-l-4 border-l-orange rounded p-4">
        <div className="text-xs text-text-mute uppercase tracking-wide mb-1">{label}</div>
        <div className="text-sm text-orange mb-2">⚠ {error}</div>
        <button
          type="button"
          onClick={onRetry}
          className="text-xs text-orange font-semibold hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`bg-paper border border-line border-l-4 ${KPI_ACCENT_BORDER[accent]} rounded p-4 text-left hover:shadow transition-shadow w-full`}
    >
      <div className="text-xs text-text-mute uppercase tracking-wide mb-1">{label}</div>
      <div className="text-3xl font-bold text-navy mb-1">{value}</div>
      <div className="text-xs text-text-mute">{subtitle}</div>
    </button>
  );
}

function SkeletonRows({ count }: { count: number }) {
  return (
    <ul className="space-y-3 animate-pulse">
      {Array.from({ length: count }).map((_, i) => (
        <li key={i} className="flex items-center gap-3">
          <div className="h-6 w-6 bg-line rounded-full" />
          <div className="flex-1">
            <div className="h-3 w-3/4 bg-line rounded mb-1.5" />
            <div className="h-2 w-1/3 bg-line rounded" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function SectionError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm flex items-center justify-between gap-3">
      <span>⚠ {message}</span>
      <button
        type="button"
        onClick={onRetry}
        className="text-xs text-orange font-semibold hover:underline shrink-0"
      >
        Retry
      </button>
    </div>
  );
}

function QuickAction({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="border border-line-dark rounded px-3 py-2.5 text-sm font-semibold text-navy hover:border-orange hover:text-orange transition-colors text-center"
    >
      {label}
    </button>
  );
}
